import React from 'react';
import { defineMessages, injectIntl, FormattedMessage } from 'react-intl';
import Icon from 'brastrap/common/icon/Icon';
import Modal from 'brastrap/common/modal/Modal';
import { heroImage1, heroImage2, heroImage3 } from './size-guide-link-images';
import {
  howItWorks,
  howItWorksUS,
  howItWorksMobile,
  howItWorksMobileUS,
} from '../../../../bra-fit-finder/images/icons';
import {
  virtualFitGif,
  phoneGif,
  liveChatGif,
  openSignGif,
} from './siize-guide-link-gifs';

type Props = {
  url: string,
  category: string,
  locale: string,
  onModalOpen: string => void,
  modalId: String,
  isOpen: Boolean,
  onModalClose: () => void,
};

const STYLE_PREFIX = 'c-size-guide-link';

const messages = defineMessages({
  label: {
    id: 'size-guide-link.label',
    defaultMessage: 'Bra size guide',
  },
});

const SizeGuideLink = (props: Props) => {
  const heroImageWithTextData = [
    {
      title: 'How should your band fit?',
      number: 1,
      text:
        'Most of the support comes from the underband. It should be horizontal around your body without riding up or digging in. We recommend fastening a new bra on its loosest hooks so that you can tighten it if it stretches over time.',
      src: heroImage1,
    },
    {
      title: 'Where should the underwire of a bra sit?',
      number: 2,
      text:
        'The underwires in your bra should sit flat on your ribcage (not your boob!) all the way round to your armpits. The wires between your boobs should lie flat against your chest without digging in, rubbing or poking out.',
      src: heroImage2,
    },
    {
      title: 'How should a bra cup fit?',
      number: 3,
      text:
        'Make sure your boobs are fully in the cups – a bit of a scoop, wiggle and jiggle usually does the trick! You should fill the cups without any bulging or wrinkling even in a lower-cut style and there should be a smooth line, with no ridge, where the top and the side of the fabric ends.',
      src: heroImage3,
    },
  ];

  /**
   * Handles click on link, buttonlinks do not seem to work well in modal, overlay still remains
   * @param {Object} event
   * @param {string} url
   * @param {string} isPhoneNumber
   */
  function onLinkClick(event, url, isPhoneNumber) {
    if (isPhoneNumber) {
      window.location.href = `tel:${url}`;
    } else {
      window.location.href = `${window.location.protocol}//${
        window.location.host
      }${window.location.href.indexOf('/us/') > -1 ? `/us/${url}` : `/${url}`}`;
    }
    props.onModalClose(props.modalId);
    event.stopPropagation();
  }

  const channelsData = locale => {
    const data = [
      {
        title: 'In-Store',
        gif: openSignGif,
        text: (
          <>
            <span>Come and see us in our </span>
            <a
              className={`${STYLE_PREFIX}__channel-link`}
              onClick={e => onLinkClick(e, 'shops', false)}
            >
              {' '}
              shops{' '}
            </a>
            <span>
              for expert bra fittings and an uplifting bra shopping experience.
            </span>
          </>
        ),
      },
      {
        title: 'Virtual fit',
        gif: virtualFitGif,
        text:
          'Get that fitting-room feeling from the comfort of your own home with a ',
        linkText: 'virtual fitting.',
        linkUrl: 'virtual-fitting',
        isPhoneNumber: false,
      },
      {
        title: 'Over the Phone',
        gif: phoneGif,
        text: `We’re experts at bra fitting over the phone! Call us${
          locale === 'en-US' ? ' toll free ' : ' '
        }on `,
        linkText: locale === 'en-GB' ? '01926 459 859.' : '+1 877 826 3857.',
        linkUrl: locale === 'en-GB' ? '+441926459859' : '+18778263857',
        isPhoneNumber: true,
      },
      {
        title: 'Live Chat',
        gif: liveChatGif,
        text:
          'Instant fitting advice while you shop! We’re real people ready to give you personalized fitting advice! ',
        linkText: 'Click here to chat!',
        linkUrl: 'live-chat',
        isPhoneNumber: false,
      },
    ];

    if (locale === 'en-US') {
      const usData = data.reduce((acc, item) => {
        if (item.title !== 'In-Store') {
          acc.push(item);
        }
        return acc;
      }, []);
      return usData;
    }
    return data;
  };

  const footer = [
    <a
      id="read-our-full-size-guide-btn"
      className="c-button-link c-button-link--major-filled c-size-guide-link-button-modal"
      onClick={e => onLinkClick(e, 'bra-fitting-guide', false)}
    >
      Read our full size guide
    </a>,
  ];

  return (
    <div
      id="bra-guide-link-modal"
      className={`${STYLE_PREFIX}__container`}
      onClick={e => {
        e.preventDefault();
        props.onModalOpen(props.modalId);
      }}
      aria-label="Open bra size guide"
    >
      <Icon className={`${STYLE_PREFIX}__icon`} icon={'size-guide-lingerie'} />
      <FormattedMessage
        className={`${STYLE_PREFIX}__text`}
        {...messages.label}
      />
      <Modal
        isOpen={props.isOpen}
        modalId={props.modalId}
        modifiers={['bra-size-guide']}
        footer={footer}
        title={'Our Guide to a Great Fit!'}
        actions={{
          dismiss: {
            icon: {
              icon: 'dismiss',
              label: 'Close',
              labelled: 'after',
            },
            onClick: e => {
              props.onModalClose(props.modalId);
              e.stopPropagation();
            },
          },
        }}
      >
        <div className={`${STYLE_PREFIX}__modal-content`}>
          <div>
            Did you know we don&apos;t use tape measures at Bravissimo? Instead
            we fit by eye as knowing how a bra should look and feel is the best
            way to find your most comfortable fit - here&apos;s how…
          </div>
          <h1>How do you know if your bra fits right?</h1>
          <div className={`${STYLE_PREFIX}__hero-images-container`}>
            {heroImageWithTextData.map((item, key) => (
              <div
                className={`${STYLE_PREFIX}__hero-image-container`}
                key={key}
              >
                <img className={`${STYLE_PREFIX}__hero-image`} src={item.src} />
                <div className={`${STYLE_PREFIX}__hero-number`}>
                  {item.number}
                </div>
                <div className={`${STYLE_PREFIX}__hero-image-title`}>
                  {item.title}
                </div>
                <div className={`${STYLE_PREFIX}__hero-image-text`}>
                  {item.text}
                </div>
              </div>
            ))}
          </div>
          <h1>Unsure of your size? Use our bra fit finder!</h1>
          <img
            className={`${STYLE_PREFIX}__size-quiz-image`}
            src={props.locale === 'en-GB' ? howItWorks : howItWorksUS}
            alt="How it works"
          />
          <img
            className={`${STYLE_PREFIX}__size-quiz-image--mobile`}
            src={
              props.locale === 'en-GB' ? howItWorksMobile : howItWorksMobileUS
            }
            alt="How it works"
          />
          <a
            className="c-button-link c-button-link--small c-button-link--major-filled"
            onClick={e => onLinkClick(e, 'bra-fit-finder', false)}
          >
            TAKE THE QUIZ
          </a>
          <div>
            Not sure what size you are, or feeling uncomfortable in your current
            size? Don’t worry, we’re here to help! A whopping 92% of women
            change bra size after their first fitting with us, so you’re not
            alone! We promise we can find you the uplifting fit that you
            deserve.
          </div>
          <div className={`${STYLE_PREFIX}__channels-container`}>
            {channelsData(props.locale).map((item, key) => (
              <div className={`${STYLE_PREFIX}__channel-container`} key={key}>
                <img
                  className={`${STYLE_PREFIX}__channel-gif`}
                  src={item.gif}
                />
                <div className={`${STYLE_PREFIX}__channel-title`}>
                  {item.title}
                </div>
                <div className={`${STYLE_PREFIX}__channel-text`}>
                  {item.text}

                  {!(item.title === 'In-Store' && props.locale === 'en-GB') ? (
                    <a
                      className={`${STYLE_PREFIX}__channel-link`}
                      onClick={e =>
                        onLinkClick(e, item.linkUrl, item.isPhoneNumber)
                      }
                    >
                      {item.linkText}
                    </a>
                  ) : (
                    <></>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className={`${STYLE_PREFIX}__returns-and-exchanges-container`}>
            <div
              className={`${STYLE_PREFIX}__returns-and-exchanges-text-container`}
            >
              <div
                className={`${STYLE_PREFIX}__returns-and-exchanges-text--enkel`}
              >
                YOU
              </div>{' '}
              <div
                className={`${STYLE_PREFIX}__returns-and-exchanges-text--editors-note`}
              >
                don&apos;t feel good?
              </div>{' '}
              <div
                className={`${STYLE_PREFIX}__returns-and-exchanges-text--enkel`}
              >
                WE
              </div>
              <div
                className={`${STYLE_PREFIX}__returns-and-exchanges-text--editors-note`}
              >
                don&apos;t feel good!
              </div>
            </div>
            <div>
              Doesn&apos;t fit quite right? Contact us to find your fit with
              60-day free returns and exchanges!
            </div>
            <div className={`${STYLE_PREFIX}__returns-and-exchanges-cta`}>
              <a
                className="c-button-link c-button-link--white-filled"
                onClick={e => onLinkClick(e, 'returns-and-exchanges', false)}
              >
                {props.locale === 'en-GB'
                  ? 'FREE RETURNS AND EXCHANGES'
                  : 'FREE US RETURNS AND EXCHANGES'}
              </a>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default injectIntl(SizeGuideLink);
